import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { D, formatDecimal } from '@alafec/shared';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { OtherAssetsSection } from '../components/positions/other-assets-section';
import { PositionsTable } from '../components/positions/positions-table';
import { useAssets, useChains, useDashboardSummary } from '../lib/api/hooks/use-queries';
import { resolveChain } from '../lib/chains';

function PositionsPage(): JSX.Element {
  const navigate = useNavigate();
  const summary = useDashboardSummary();
  const chains = useChains();
  const assets = useAssets();

  if (summary.isLoading) return <Skeleton className="h-48" />;

  const rows = (summary.data?.positions ?? []).map((p) => {
    const chain = resolveChain(p.chainId, chains.data);
    return {
      assetId: p.assetId,
      symbol: p.symbol,
      chainName: chain.name,
      chainSlug: chain.slug,
      qty: p.qty,
      cumpEur: p.cumpEur,
      spotEur: p.spotEur,
      valueEur: p.valueEur,
      pnlEur: p.unrealizedPlEur,
      // Raw percentage value (e.g. "12.50"); the table renders the "%" suffix.
      allocationPct: formatDecimal(D(p.allocationPct), 2),
      valued: p.valued,
    };
  });

  const positionAssetIds = new Set(rows.map((r) => r.assetId));
  const otherAssets = (assets.data ?? [])
    .filter((a) => !positionAssetIds.has(a.id))
    .map((a) => {
      const chain = resolveChain(a.chainId, chains.data);
      return {
        assetId: a.id,
        symbol: a.symbol,
        name: a.name,
        chainName: chain.name,
        chainSlug: chain.slug,
      };
    })
    .toSorted((a, b) => a.symbol.localeCompare(b.symbol, 'fr'));

  const openAsset = (assetId: number): void => {
    void navigate({ to: '/assets/$assetId', params: { assetId: String(assetId) } });
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Positions ouvertes</CardTitle>
        </CardHeader>
        <CardContent>
          <PositionsTable rows={rows} onRowClick={openAsset} />
        </CardContent>
      </Card>
      <OtherAssetsSection rows={otherAssets} onAssetClick={openAsset} />
    </div>
  );
}

export const Route = createFileRoute('/positions')({ component: PositionsPage });
